import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { getEcho } from '../echo';
import chatService from '../services/chatService';
import { notify } from '../components/CustomToast';
import { recordDeletion, reconcileDeletions, mergeMessageLists } from '../components/Talk/messageDeletion';

const ChatContext = createContext();

export const useChat = () => useContext(ChatContext);

export const ChatProvider = ({ children }) => {
    const [conversations, setConversations] = useState([]);
    const [activeConversationId, setActiveConversationId] = useState(null);
    const [hideSidebar, setHideSidebar] = useState(false);
    const [messages, setMessages] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);
    const [unreadTotal, setUnreadTotal] = useState(0);

    // Track last fetch time to prevent duplicate calls
    const lastFetchTimeRef = useRef(0);
    const fetchInProgressRef = useRef(false);
    const deletionLedger = useRef(new Map());
    const [deletionRevision, setDeletionRevision] = useState(0);
    const conversationRequest = useRef(0);
    const reconcileMessages = useCallback(list => reconcileDeletions(list, deletionLedger.current), []);
    const mergeMessages = useCallback((current, incoming) => mergeMessageLists(current, incoming, deletionLedger.current), []);

    const applyDeletion = useCallback(event => {
        if (!recordDeletion(deletionLedger.current, event)) return;
        setDeletionRevision(value => value + 1);
        setConversations(current => current.map(chat => {
            if (String(chat.id) !== String(event.conversation_id) || !chat.last_message) return chat;
            const visible = reconcileDeletions([chat.last_message], deletionLedger.current);
            return { ...chat, last_message: visible[0] || null };
        }));
    }, []);

    const rememberHiddenMessages = useCallback((ids, conversationId) => {
        ids.forEach(id => applyDeletion({ message_id: id, conversation_id: conversationId, scope: 'me' }));
    }, [applyDeletion]);

    // Load conversations with pagination
    // Load conversations with pagination
    const fetchConversations = useCallback(async (pageToLoad = 1, force = false) => {
        let requestId;
        try {
            // Prevent duplicate calls within 500ms for page 1
            const now = Date.now();
            if (pageToLoad === 1) {
                if (!force && (fetchInProgressRef.current || now - lastFetchTimeRef.current < 500)) {
                    console.log('ChatContext: Skipping duplicate fetch request');
                    return;
                }
                fetchInProgressRef.current = true;
                lastFetchTimeRef.current = now;
                setIsLoading(true);
            } else {
                setIsLoadingMore(true);
            }

            requestId = ++conversationRequest.current;

            const response = await chatService.getConversations(pageToLoad);
            if (requestId !== conversationRequest.current) return;
            console.log('ChatContext: raw response', response);

            // Response interceptor unwraps response.data, so response = { code, status, message, data }
            let list = [];
            let lastPage = 1;
            let currentPage = 1;

            if (response && response.data) {
                const dataField = response.data;

                if (Array.isArray(dataField)) {
                    // Direct array of conversations
                    list = dataField;
                    console.log('ChatContext: found conversations array', list.length);
                } else if (dataField && Array.isArray(dataField.data)) {
                    // Paginated response
                    list = dataField.data;
                    lastPage = dataField.last_page || 1;
                    currentPage = dataField.current_page || 1;
                    console.log('ChatContext: found paginated conversations', list.length);
                } else {
                    console.warn('ChatContext: unexpected data format', dataField);
                }
            } else {
                console.warn('ChatContext: invalid response structure', response);
            }

            console.log('ChatContext: extracted conversations', list);

            list = list.map(chat => ({ ...chat, last_message: chat.last_message
                ? reconcileDeletions([chat.last_message], deletionLedger.current)[0] || null : null }));
            if (pageToLoad === 1) {
                setConversations(list);
            } else {
                setConversations(prev => {
                    // Filter duplicates based on ID
                    const newIds = new Set(list.map(c => c.id));
                    return [...prev.filter(c => !newIds.has(c.id)), ...list];
                });
            }

            setPage(currentPage);
            setHasMore(currentPage < lastPage);

        } catch (error) {
            console.error('Failed to load conversations', error);
        } finally {
            if (requestId === conversationRequest.current) {
                setIsLoading(false);
                setIsLoadingMore(false);
                fetchInProgressRef.current = false;
            }
        }
    }, []);

    // Load next page
    const loadMoreConversations = useCallback(() => {
        if (!isLoadingMore && hasMore) {
            fetchConversations(page + 1);
        }
    }, [isLoadingMore, hasMore, page, fetchConversations]);

    // Track active conversation ID in ref to access in listeners without re-subscribing
    const activeConversationIdRef = useRef(activeConversationId);
    useEffect(() => {
        activeConversationIdRef.current = activeConversationId;
    }, [activeConversationId]);

    // Mark conversation as read when it becomes active
    useEffect(() => {
        if (activeConversationId) {
            // Update local state to clear unread count immediately
            setConversations(prev => prev.map(c => {
                if (c.id === activeConversationId && (c.unread_count > 0 || c.unread_count === undefined)) {
                    return { ...c, unread_count: 0 };
                }
                return c;
            }));

            // Call API (fire and forget)
            chatService.markAsRead(activeConversationId).catch(err => console.error('Failed to mark read', err));
        }
    }, [activeConversationId]);

    // Track latest fetchConversations function
    const fetchConversationsRef = useRef(fetchConversations);
    useEffect(() => {
        fetchConversationsRef.current = fetchConversations;
    }, [fetchConversations]);

    const handleNewMessage = useCallback((message) => {
        if (!message) return;
        message = reconcileDeletions([message], deletionLedger.current)[0];
        if (!message || message.deleted_at) return;
        console.log('ChatContext: Handling new message', message);

        setConversations(prev => {
            const existingIndex = prev.findIndex(c => c.id === message.conversation_id);
            let newConversations = [...prev];
            let conversation;

            if (existingIndex > -1) {
                // Update existing
                conversation = { ...newConversations[existingIndex] };
                conversation.last_message = message;
                conversation.updated_at = message.created_at;

                // Increment unread count if message is not from current user and not viewing this conversation
                const user = JSON.parse(localStorage.getItem('user'));
                const isActive = activeConversationIdRef.current === message.conversation_id;
                const isFromMe = message.sender_id === user?.id;

                if (!isActive && !isFromMe) {
                    conversation.unread_count = (conversation.unread_count || 0) + 1;
                } else if (isActive) {
                    conversation.unread_count = 0;
                }

                // Remove from current position
                newConversations.splice(existingIndex, 1);
            } else {
                // If not found, we might need to fetch it or create a placeholder
                // For now, triggering a refresh is safer, but we can try to fetch just this one
                console.log('ChatContext: Conversation not found, refreshing list');
                fetchConversationsRef.current(1);
                return prev;
            }

            // Add to top
            return [conversation, ...newConversations];
        });

        // Notifications
        const user = JSON.parse(localStorage.getItem('user'));
        const isNotFromMe = message ? (message.sender_id !== user?.id) : false;
        const isNotActiveConversation = activeConversationIdRef.current !== message.conversation_id;

        if (isNotFromMe && isNotActiveConversation && 'Notification' in window && Notification.permission === 'granted') {
            const notificationTitle = message.sender?.name || 'New Message';
            const notificationBody = message.kind === 'attachment'
                ? 'Sent an attachment'
                : (message.body || 'Sent a message');

            const notification = new Notification(notificationTitle, {
                body: notificationBody,
                icon: message.sender?.avatar || '/logo192.png',
                tag: `talk-${message.conversation_id}`,
                requireInteraction: false
            });

            notification.onclick = () => {
                window.focus();
                setActiveConversationId(message.conversation_id);
                notification.close();
            };
        }
    }, [setActiveConversationId]);

    // Global Echo Listeners
    useEffect(() => {
        const user = JSON.parse(localStorage.getItem('user'));
        if (!user) return;

        const echo = getEcho();
        if (!echo) {
            console.warn('Echo not initialized');
            return;
        }

        console.log('ChatContext: Activating Chat Listeners for User:', user.id);
        const channel = echo.private(`App.Models.User.${user.id}`);

        channel.listen('.MessageSent', (e) => {
            console.log('ChatContext: MessageSent event received', e);
            handleNewMessage(e.message);
        });

        channel.listen('.ConversationUpdated', (e) => {
            console.log('ChatContext: ConversationUpdated event received', e);
            // Optional: check if we actually need to refresh
            fetchConversationsRef.current(1);
        });

        const deleted = event => {
            applyDeletion(event);
            fetchConversationsRef.current(1, true);
        };
        channel.listen('.talk.message.deleted', deleted);
        const connection = echo.connector?.pusher?.connection;
        const reconnect = () => fetchConversationsRef.current(1, true);
        connection?.bind('connected', reconnect);

        channel.notification((notification) => {
            console.log('ChatContext: Notification received', notification);
            // Handle generic notifications if strictly needed outside of MessageSent
            fetchConversationsRef.current(1);
        });

        return () => {
            if (channel) {
                channel.stopListening('.MessageSent');
                channel.stopListening('.ConversationUpdated');
                channel.stopListening('.talk.message.deleted', deleted);
            }
            connection?.unbind('connected', reconnect);
        };
    }, [handleNewMessage, applyDeletion]);

    /**
     * Open or create a team conversation
     * @param {number} teamId - The team ID to open chat for
     * @returns {Promise<number|null>} - Returns conversation ID on success, null on error
     */
    const openTeamChat = useCallback(async (teamId) => {
        try {
            setIsLoading(true);
            const response = await chatService.createConversation({
                type: 'team',
                team_id: teamId
            });

            if (response.status && response.data?.conversation) {
                const conversation = response.data.conversation;

                // Add to conversations list if not already present
                setConversations(prev => {
                    const exists = prev.find(c => c.id === conversation.id);
                    if (!exists) {
                        return [conversation, ...prev];
                    }
                    return prev;
                });

                // Set as active conversation and hide sidebar (came from Teams)
                setActiveConversationId(conversation.id);
                setHideSidebar(true);

                return conversation.id;
            } else {
                notify('error', response.message || 'Failed to open team chat');
                return null;
            }
        } catch (error) {
            console.error('Failed to open team chat:', error);
            notify('error', error.message || 'Failed to open team chat');
            return null;
        } finally {
            setIsLoading(false);
        }
    }, []);

    const value = {
        conversations,
        activeConversationId,
        messages,
        isLoading,
        isLoadingMore,
        hasMore,
        unreadTotal,
        hideSidebar,
        setHideSidebar,
        setActiveConversationId,
        fetchConversations,
        loadMoreConversations,
        deletionRevision,
        applyDeletion,
        reconcileMessages,
        mergeMessages,
        rememberHiddenMessages,
        openTeamChat
    };

    return (
        <ChatContext.Provider value={value}>
            {children}
        </ChatContext.Provider>
    );
};
